<template>
	<div
		class="graph-card"
		:class="[type, `type-${graphType}`]"
		:style="{
			'--reachedBackgroundColor': reachedBackgroundColor,
			'--reachedColor': reachedColor,
			'--reachedHoverBackgroundColor': reachedHoverBackgroundColor,
			'--notReachedBackgroundColor': notReachedBackgroundColor,
			'--notReachedColor': notReachedColor,
			'--aboveBackgroundColor': aboveBackgroundColor,
			'--aboveColor': aboveColor,
			'--noGoalBackgroundColor': noGoalBackgroundColor,
			'--noGoalColor': noGoalColor,
		}"
	>
		<div class="graph-header">
			<h3>{{ title }}</h3>
		</div>
		<div class="graph-main">
			<template v-if="graphType === 'progress'">
				<div class="graph-body">
					<div class="progress-container">
						<div
							class="indicator-container"
							:class="{
								noGoal: goal === null,
							}"
						>
							<div
								class="indicator-wrapper"
								@click="ChangeGoalQuestion(type)"
							>
								<span class="goal-value">{{ goal }}</span>
								<i class="fas fa-caret-down"></i>
							</div>
						</div>
						<div class="progress-bar">
							<div
								class="progress"
								:class="{
									above: percentageValue > percentageGoal,
									reached: percentageValue === percentageGoal,
									reverse: reverse,
									noGoal: goal === null,
								}"
								:style="{ width: percentageValue + '%' }"
							></div>
							<h2
								class="statistic-value"
								:class="{
									above: percentageValue > percentageGoal,
									reached: percentageValue === percentageGoal,
									reverse: reverse,
									noGoal: goal === null,
								}"
							>
								{{ value }}
							</h2>
						</div>
					</div>
				</div>
			</template>
			<template v-else-if="graphType === 'circular'">
				<div class="graph-body">
					<div
						class="circular-donut"
						:class="{
							above: percentageValue > percentageGoal,
							reached: percentageValue === percentageGoal,
							reverse: reverse,
							noGoal: goal === null,
						}"
						:style="{
							'--progress': animatedProgress,
							'--fontSize': donutFontSize + 'rem',
						}"
					>
						<div
							class="indicator-container"
							:class="{
								noGoal: goal === null,
							}"
						>
							<div
								class="indicator-wrapper"
								@click="ChangeGoalQuestion(type)"
							>
								<span class="goal-value">{{ goal }}</span>
								<i class="fas fa-caret-right"></i>
							</div>
						</div>
						<div class="statistic-value-container">
							<h2
								class="statistic-value"
								:class="{
									above: percentageValue > percentageGoal,
									reached: percentageValue === percentageGoal,
									reverse: reverse,
									noGoal: goal === null,
								}"
							>
								{{ value
								}}<span v-if="type === 'no_show'">%</span>
							</h2>
						</div>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script>
	import Swal from 'sweetalert2'
	import _ from 'lodash'

	export default {
		name: 'GraphCard',
		props: {
			title: {
				type: String,
				required: true,
			},
			goal: {
				type: [Number, null],
				required: false,
				default: null,
			},
			value: {
				type: [Number, Object],
				required: true,
			},
			type: {
				type: String,
				required: true,
			},
			hasFilters: {
				type: Boolean,
				default: false,
			},
			reverse: {
				type: Boolean,
				default: false,
			},
			graphType: {
				type: String,
				default: 'progress',
			},
		},
		data() {
			return {
				progressInterval: null,
				animatedProgress: 0,
				reachedBackgroundColor: 'var(--yellowBackgroundColor)',
				reachedColor: 'var(--yellowColor)',
				reachedHoverBackgroundColor:
					'var(--pendingHoverBackgroundColor)',
				notReachedBackgroundColor: 'var(--redBackgroundColor)',
				notReachedColor: 'var(--redColor)',
				aboveBackgroundColor: 'var(--blueBackgroundColor)',
				aboveColor: 'var(--blueColor)',
				noGoalBackgroundColor: 'var(--blueBackgroundColor)',
				noGoalColor: 'var(--blueColor)',
			}
		},
		computed: {
			max() {
				if (this.goal === null) return 100
				if (this.goal === 0) return 100
				return this.goal + this.goal / 3
			},
			percentageGoal() {
				if (this.goal === null) return 0
				if (this.goal === 0) return 0
				return (100 / this.max) * this.goal
			},
			percentageValue() {
				if (this.goal === null)
					return Math.min(100, (this.value / 100) * 100)
				if (this.goal === 0) return Math.min(100, this.value * 100)
				return (100 / this.max) * this.value
			},
			donutFontSize() {
				if (this.type === 'no_show') {
					const INITIAL_SIZE = 6
					const MINIMUN_SIZE = 2.5
					const MEDIUM_SIZE = 3.2

					const chars = `${this.value}`.split('').length + 1

					if (chars > 2 && chars < 5) return MEDIUM_SIZE
					if (chars >= 5) return MINIMUN_SIZE

					return INITIAL_SIZE
				}

				const INITIAL_SIZE = 6
				const MINIMUN_CHARS = 2
				const MINIMUN_SIZE = 4.2

				const chars = `${this.value}`.split('').length
				if (chars <= MINIMUN_CHARS) return INITIAL_SIZE

				const diff = INITIAL_SIZE - chars
				if (diff <= 0) return MINIMUN_SIZE

				const size = INITIAL_SIZE - diff + diff / 100
				if (size < MINIMUN_SIZE) return MINIMUN_SIZE

				return size
			},
			distributionData() {
				if (typeof this.value !== 'object') return []

				const total = Object.values(this.value).reduce(
					(acc, val) => acc + val,
					0
				)

				const distribution = _.orderBy(
					Object.entries(this.value).map(([key, val]) => ({
						label: key,
						value: val,
						percentage: total === 0 ? 0 : (val / total) * 100,
					})),
					'label',
					'asc'
				)

				const nowHour = new Date().getHours()
				const nowIndex = distribution.findIndex(
					item => Number(item.label) === nowHour
				)
				const first_slice = distribution.slice(0, nowIndex + 1)
				const second_slice = distribution.slice(
					nowIndex + 1,
					distribution.length
				)

				const sortedDistribution = [...second_slice, ...first_slice]

				return sortedDistribution
			},
		},
		watch: {
			percentageValue: {
				handler(newVal, oldVal) {
					if (this.progressInterval) {
						clearInterval(this.progressInterval)
						this.progressInterval = null
					}

					if (
						typeof oldVal !== 'number' ||
						Math.abs(newVal - oldVal) < 1
					) {
						this.animatedProgress = newVal
						return
					}

					if (this.animatedProgress > 100) this.animatedProgress = 100
					if (this.animatedProgress < 0) this.animatedProgress = 0

					const step = newVal > oldVal ? 1 : -1

					this.progressInterval = setInterval(() => {
						if (this.animatedProgress === newVal) {
							clearInterval(this.progressInterval)
							this.progressInterval = null
							return
						}
						this.animatedProgress += step

						if (
							(step > 0 && this.animatedProgress > newVal) ||
							(step < 0 && this.animatedProgress < newVal)
						) {
							this.animatedProgress = newVal
							clearInterval(this.progressInterval)
							this.progressInterval = null
						}
					}, 5)
				},
				immediate: true,
			},
		},
		methods: {
			ChangeGoalQuestion(key) {
				Swal.fire({
					title: 'PREENCHA COM A NOVA META',
					customClass: {
						container: 'themed change-goal-modal',
					},
					input: 'text',
					inputValue:
						this.goal !== null && this.goal !== undefined
							? String(this.goal)
							: '',
					inputValidator: value => {
						if (value !== '' && isNaN(value)) {
							return 'PRECISA SER UM NÚMERO VÁLIDO!'
						}

						if (value < 0) {
							return 'PRECISA SER UM NÚMERO MAIOR OU IGUAL A 0!'
						}
					},
					showCancelButton: true,
					reverseButtons: true,
					cancelButtonText: 'CANCELAR',
					confirmButtonText: 'SALVAR',
				}).then(response => {
					if (response.isConfirmed) {
						this.$emit('change-goal', {
							key,
							value:
								response.value === '' ? null : +response.value,
						})
					}
				})
			},
		},
	}
</script>

<style lang="scss" scoped>
	.graph-card {
		--reachedBackgroundColor: #f5c747;
		--notReachedBackgroundColor: #fe0f38;
		--aboveBackgroundColor: #105dd9;
		--noGoalColor: #f5c747;

		padding: 1rem;
		background-color: var(--elementsBackgroundColor);
		border: 1px solid var(--elementsBackgroundColor);
		border-radius: 1rem;
		display: flex;
		flex-direction: column;
		align-items: center;

		.graph-header {
			width: 100%;
			display: flex;

			h3 {
				color: var(--cardfontColor);
				font-size: 1.25rem;
				line-height: 1.75rem;
				text-transform: uppercase;
				white-space: nowrap;
				text-overflow: ellipsis;
				overflow: hidden;
				margin-bottom: 0;
			}
		}

		.graph-main {
			width: 100%;
			flex-grow: 1;
			display: flex;
			justify-content: center;
			align-items: center;
			flex-direction: column;

			.graph-body {
				width: 100%;
				flex-grow: 1;
				display: flex;
				align-items: center;
				gap: 3rem;

				.progress-container {
					width: 100%;
					position: relative;
					margin-top: 2rem;

					.indicator-container {
						--indicatorSize: 3rem;
						width: 100%;
						height: 3rem;
						// overflow: hidden;
						position: relative;

						.indicator-wrapper {
							display: flex;
							flex-direction: column;
							justify-content: center;
							align-items: center;
							font-size: var(--indicatorSize);
							transform: translateX(-50%);
							position: absolute;
							bottom: 0.1rem;
							left: 75%;
							cursor: pointer;

							.goal-value {
								// position: absolute;
								// right: 2.3rem;
								font-size: 1.5rem;
								font-weight: 900;
								color: var(--fontColor);
							}

							i {
								display: flex;
								justify-content: center;
								align-items: center;
								color: var(--reachedBackgroundColor);
								height: 1.8rem;
							}

							&:hover {
								i {
									color: var(--reachedHoverBackgroundColor);
								}
							}
						}

						&.noGoal {
							.indicator-wrapper {
								i {
									opacity: 0.3;
								}
							}
						}
					}

					.progress-bar {
						width: 100%;
						height: 5.313rem;
						background-color: var(--graphBackgroundColor);
						border-radius: 10px;
						overflow: hidden;
						position: relative;

						.progress {
							height: 100%;
							transition: width 0.3s ease;

							background-color: var(--notReachedBackgroundColor);
							&.reached {
								background-color: var(--reachedBackgroundColor);
							}

							&.above {
								background-color: var(--aboveBackgroundColor);
							}

							&.noGoal {
								background-color: var(--noGoalBackgroundColor);
							}

							&.reverse {
								background-color: var(--aboveBackgroundColor);

								&.reached {
									background-color: var(
										--reachedBackgroundColor
									);
								}

								&.above {
									background-color: var(
										--notReachedBackgroundColor
									);
								}

								&.noGoal {
									background-color: var(
										--noGoalBackgroundColor
									);
								}
							}
						}
					}
				}

				.statistic-value {
					font-size: 6rem;
					margin: 0;
					width: fit-content;
					position: absolute;
					right: 0;

					&.bigger {
						font-size: 9rem;
					}

					color: var(--notReachedColor);
					&.reached {
						color: var(--reachedColor);
					}

					&.above {
						color: var(--aboveColor);
					}

					&.noGoal {
						color: var(--noGoalColor);
					}

					&.reverse {
						color: var(--aboveColor);

						&.reached {
							color: var(--reachedColor);
						}

						&.above {
							color: var(--notReachedColor);
						}

						&.noGoal {
							color: var(--noGoalColor);
						}
					}
				}
			}

			.graph-footer {
				min-width: 30%;
				display: flex;
				justify-content: center;

				.graph-legend {
					.graph-legend-container {
						display: flex;
						justify-content: center;
						align-items: baseline;
						gap: 0.8rem;
						cursor: pointer;

						i {
							color: var(--reachedBackgroundColor);
							font-size: 1.5rem;
						}

						p {
							font-size: 1.5rem;
							margin-bottom: 0;
							color: var(--cardfontColor);
						}
					}
				}
			}
		}

		&.type-circular {
			position: relative;

			.graph-main {
				position: absolute;
				top: 1rem;

				flex-direction: row;

				.graph-body {
					justify-content: center;

					.circular-donut {
						--progress: 0;
						--color: var(--notReachedBackgroundColor);
						--size: 19rem;
						--fontSize: 6rem;

						&.reached {
							--color: var(--reachedBackgroundColor);
						}

						&.above {
							--color: var(--aboveBackgroundColor);
						}

						&.noGoal {
							--color: var(--noGoalBackgroundColor);
						}

						&.reverse {
							--color: var(--aboveBackgroundColor);

							&.reached {
								--color: var(--reachedBackgroundColor);
							}

							&.above {
								--color: var(--notReachedBackgroundColor);
							}

							&.noGoal {
								--color: var(--noGoalBackgroundColor);
							}
						}

						width: var(--size);
						height: var(--size);
						border-radius: 50%;
						background: conic-gradient(
							var(--color) calc(var(--progress) * 1%),
							var(--graphBackgroundColor) 0
						);
						display: flex;
						align-items: center;
						justify-content: center;
						transition: background 0.3s ease;
						position: relative;

						.indicator-container {
							--indicatorSize: 3rem;

							width: 100%;
							height: 100%;
							position: absolute;

							.indicator-wrapper {
								display: flex;
								justify-content: center;
								align-items: center;
								gap: 0.5rem;
								font-size: var(--indicatorSize);
								transform: translateY(-50%);
								position: absolute;
								left: -1rem;
								top: 50%;
								cursor: pointer;

								.goal-value {
									font-size: 1.5rem;
									font-weight: 900;
									color: var(--fontColor);
									position: absolute;
									right: 1.7rem;
								}

								i {
									display: flex;
									justify-content: center;
									align-items: center;
									color: var(--reachedBackgroundColor);
								}

								&:hover {
									i {
										color: var(
											--reachedHoverBackgroundColor
										);
									}
								}
							}

							&.noGoal {
								.indicator-wrapper {
									i {
										opacity: 0.3;
									}
								}
							}
						}

						.statistic-value-container {
							width: calc(var(--size) - 5rem);
							height: calc(var(--size) - 5rem);
							border: 1px solid var(--elementsBackgroundColor);
							border-radius: 50%;
							background-color: var(--elementsBackgroundColor);
							display: flex;
							align-items: center;
							justify-content: center;
							z-index: 1;
						}

						.statistic-value {
							position: static;
							font-size: var(--fontSize);
							margin: 0;
							text-align: center;
							width: fit-content;
						}
					}
				}
			}
		}
	}
</style>
